import { useEffect, useState } from 'react';
import { BrandMark } from '../components/BrandMark';
import { ErrorNotice, Loading } from '../components/Feedback';
import { SenoIcon } from '../components/SenoIcon';
import { get } from '../lib/api';

type AdminSummary = {
  counts: Record<string, number>;
  aiUsage7d: Record<string, number>;
  modelMix: Array<Record<string, string | number>>;
  storage: Record<string, number>;
  errors: Array<Record<string, string | number>>;
  rateLimits: Array<Record<string, string | number>>;
  malware: Array<Record<string, string | number>>;
  users?: Array<Record<string, string | number>>;
  projects?: Array<Record<string, string | number>>;
  orders?: Array<Record<string, string | number>>;
  payments?: Array<Record<string, string | number>>;
  costByProject?: Array<Record<string, string | number>>;
  alerts?: Array<{ code: string; severity: string; count: number }>;
  cacheHitRate?: number;
};

export function AdminPage({ navigate }: { navigate: (path: string) => void }) {
  const [summary, setSummary] = useState<AdminSummary | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    get<AdminSummary>('/api/admin')
      .then(setSummary)
      .catch((caught) =>
        setError(caught instanceof Error ? caught.message : 'Dashboard gagal dimuat.')
      );
  }, []);
  return (
    <div className="admin-page">
      <header className="dashboard-header">
        <button className="brand-button" onClick={() => navigate('/app')}>
          <BrandMark />
        </button>
        <button className="button button-secondary" onClick={() => navigate('/app')}>
          Kembali
        </button>
      </header>
      <main className="admin-main">
        <div className="page-heading">
          <div>
            <span className="eyebrow">Operasional komersial</span>
            <h1>Admin SenoSkripsi</h1>
            <p>
              Metadata, pembayaran, biaya AI, rate limit, dan keamanan—tanpa membuka isi privat.
            </p>
          </div>
        </div>
        {error ? <ErrorNotice message={error} /> : null}
        {!summary ? (
          <Loading label="Mengumpulkan metrik…" />
        ) : (
          <>
            <section className="admin-metrics">
              {Object.entries(summary.counts).map(([key, value]) => (
                <article key={key}>
                  <SenoIcon name="admin" />
                  <span>{key.replaceAll('_', ' ')}</span>
                  <strong>{value}</strong>
                </article>
              ))}
            </section>
            <section className="admin-panels">
              <article>
                <h2>AI 7 hari</h2>
                <p>{summary.aiUsage7d.calls || 0} panggilan tercatat.</p>
                <p>
                  Estimasi biaya internal US$
                  {Number(summary.aiUsage7d.estimated_cost_usd || 0).toFixed(2)}
                </p>
              </article>
              <article>
                <h2>Penyimpanan</h2>
                <p>
                  {summary.storage.files || 0} berkas ·{' '}
                  {(Number(summary.storage.bytes || 0) / 1024 / 1024).toFixed(1)} MB
                </p>
              </article>
              <article>
                <h2>Malware queue</h2>
                <p>
                  {summary.malware.length
                    ? summary.malware.map((item) => `${item.scan_state}: ${item.count}`).join(' · ')
                    : 'Belum ada job'}
                </p>
              </article>
              <article>
                <h2>Rate limit</h2>
                <p>
                  {summary.rateLimits.length
                    ? summary.rateLimits.map((item) => `${item.action}: ${item.count}`).join(' · ')
                    : 'Tidak ada event'}
                </p>
              </article>
              <article>
                <h2>Cache input AI</h2>
                <p>{((summary.cacheHitRate || 0) * 100).toFixed(1)}% unit input cache 7 hari.</p>
              </article>
              <article>
                <h2>Alert aktif</h2>
                <p>
                  {summary.alerts?.length
                    ? summary.alerts.map((item) => `${item.code}: ${item.count}`).join(' · ')
                    : 'Tidak ada alert agregat'}
                </p>
              </article>
            </section>
            <section className="admin-commercial-table" aria-label="Aktivitas komersial terbaru">
              <div>
                <h2>Order terbaru</h2>
                {(summary.orders || []).slice(0, 8).map((order) => (
                  <p key={String(order.id)}>
                    <strong>{String(order.plan_code)}</strong> · {String(order.status)} · Rp
                    {Number(order.amount).toLocaleString('id-ID')}
                  </p>
                ))}
                {!summary.orders?.length ? <p>Belum ada order.</p> : null}
              </div>
              <div>
                <h2>Biaya per proyek</h2>
                {(summary.costByProject || []).slice(0, 8).map((project) => (
                  <p key={String(project.project_id)}>
                    <strong>{String(project.name)}</strong> · US$
                    {Number(project.cost_usd).toFixed(2)}
                  </p>
                ))}
                {!summary.costByProject?.length ? <p>Belum ada biaya terkomit.</p> : null}
              </div>
            </section>
            <section className="privacy-boundary">
              <SenoIcon name="security" />
              <div>
                <strong>Private by default</strong>
                <p>
                  Admin hanya menerima agregat. Isi dokumen memerlukan support grant aktif, terbatas
                  scope, memiliki expiry, dan diaudit.
                </p>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
